import { useState, useEffect, useCallback } from 'react';
import { Grid, Box } from '@mui/material';
import { getActiveClearBoxConfig } from '../../services/api';

import ClearBoxCarousel from './Components/ClearBox/ClearBoxCarousel';
import ClearBoxConfigPanel from './Components/ClearBox/ClearBoxConfigPanel';

const imageBaseUrl = process.env.PUBLIC_URL || '';

const MAX_MACHINES = 10;

function createMachine(index) {
    return { id: `machine-${Date.now()}-${index}`, name: `Machine ${index}`, answers: {} };
}

const imageSources = [

    {
        id: 'clearbox-vertical',
        src: `${imageBaseUrl}/static/images/ClearBox/clear_box_1.jpg`,
        alt: 'ClearBox Vertical view',
    },
    {
        id: 'clearbox-hero',
        src: `${imageBaseUrl}/static/images/ClearBox/clear_box_4.jpg`,
        alt: 'ClearBox horizontal view.',
    },
    {
        id: 'clearbox-open-board',
        src: `${imageBaseUrl}/static/images/ClearBox/clear_box_5.jpg`,
        alt: 'ClearBox horizontal view.',
    },
];

export default function ClearBoxBuy() {
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [machines, setMachines] = useState([createMachine(1)]);
    const [activeMachineIndex, setActiveMachineIndex] = useState(0);
    const [questionnaire, setQuestionnaire] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchConfig = () => {
        setIsLoading(true);
        setError(null);
        getActiveClearBoxConfig()
            .then((res) => {
                if (res?.data) setQuestionnaire(res.data);
            })
            .catch((err) => {
                console.error('Could not fetch ClearBox config from API:', err.message);
                setError(err.message || 'Failed to load configuration');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    const handleAnswerChange = useCallback((questionId, value) => {
        setMachines((prev) => {
            const updated = [...prev];
            updated[activeMachineIndex] = {
                ...updated[activeMachineIndex],
                answers: { ...updated[activeMachineIndex].answers, [questionId]: value },
            };
            return updated;
        });
    }, [activeMachineIndex]);

    const addMachine = useCallback(() => {
        setMachines((prev) => {
            if (prev.length >= MAX_MACHINES) return prev;
            const newMachine = createMachine(prev.length + 1);
            return [...prev, newMachine];
        });
        setActiveMachineIndex((prev) => prev + 1);
    }, []);

    const removeMachine = useCallback((index) => {
        setMachines((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== index);
        });
        setActiveMachineIndex((prev) => {
            if (index <= prev && prev > 0) return prev - 1;
            return prev;
        });
    }, []);

    const renameMachine = useCallback((index, name) => {
        setMachines((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], name };
            return updated;
        });
    }, []);

    const duplicateMachine = useCallback((index) => {
        setMachines((prev) => {
            if (prev.length >= MAX_MACHINES) return prev;
            const source = prev[index];
            if (!source) return prev;
            const clone = {
                id: `machine-${Date.now()}-${prev.length}`,
                name: `${source.name} (copy)`,
                answers: structuredClone(source.answers || {}),
            };
            const updated = [...prev];
            updated.splice(index + 1, 0, clone);
            return updated;
        });
        setActiveMachineIndex(index + 1);
    }, []);

    return (
        <Box
            className="clearbox-buy-page"
            sx={{
                width: '100%',
                mt: 0,
                minHeight: { xs: '100vh', md: 'calc(100vh - 90px)' },
                height: { md: 'calc(100vh - 90px)' },
                overflow: { md: 'hidden' },
            }}
        >
            <Grid container spacing={0} alignItems="stretch" sx={{ height: '100%' }}>
                {/* Left: Sticky image carousel */}
                <Grid
                    item
                    xs={12}
                    md={8}
                    sx={{
                        height: { md: '100%' },
                    }}
                >
                    <ClearBoxCarousel
                        imageSources={imageSources}
                        activeImageIndex={activeImageIndex}
                        onChangeActiveImage={setActiveImageIndex}
                    />
                </Grid>

                {/* Right: Configuration panel */}
                <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                        height: { md: '100%' },
                    }}
                >
                    <ClearBoxConfigPanel
                        questionnaire={questionnaire}
                        machines={machines}
                        activeMachineIndex={activeMachineIndex}
                        onActiveMachineChange={setActiveMachineIndex}
                        onAnswerChange={handleAnswerChange}
                        onAddMachine={addMachine}
                        onRemoveMachine={removeMachine}
                        onRenameMachine={renameMachine}
                        onDuplicateMachine={duplicateMachine}
                        maxMachines={MAX_MACHINES}
                        isLoading={isLoading}
                        error={error}
                        onRetry={fetchConfig}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

